/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {AnimateTimings, ɵStyleData} from '@angular/animations';

export const /** @type {?} */ ONE_SECOND = 1000;
/**
 * @param {?} exp
 * @param {?} errors
 * @return {?}
 */
export function parseTimeExpression(exp: string | number, errors: string[]): AnimateTimings {
  const /** @type {?} */ regex = /^([\.\d]+)(m?s)(?:\s+([\.\d]+)(m?s))?(?:\s+([-a-z]+(?:\(.+?\))?))?$/i;
  let /** @type {?} */ duration: number;
  let /** @type {?} */ delay: number = 0;
  let /** @type {?} */ easing: string|null = null;
  if (typeof exp === 'string') {
    const /** @type {?} */ matches = exp.match(regex);
    if (matches === null) {
      errors.push(`The provided timing value "${exp}" is invalid.`);
      return {duration: 0, delay: 0, easing: null};
    }

    let /** @type {?} */ durationMatch = parseFloat(matches[1]);
    const /** @type {?} */ durationUnit = matches[2];
    if (durationUnit == 's') {
      durationMatch *= ONE_SECOND;
    }
    duration = Math.floor(durationMatch);

    const /** @type {?} */ delayMatch = matches[3];
    const /** @type {?} */ delayUnit = matches[4];
    if (delayMatch != null) {
      let /** @type {?} */ delayVal: number = parseFloat(delayMatch);
      if (delayUnit != null && delayUnit == 's') {
        delayVal *= ONE_SECOND;
      }
      delay = Math.floor(delayVal);
    }

    const /** @type {?} */ easingVal = matches[5];
    if (easingVal) {
      easing = easingVal;
    }
  } else {
    duration = /** @type {?} */(( <number>exp));
  }

  return {duration, delay, easing};
}
/**
 * @param {?} styles
 * @return {?}
 */
export function normalizeStyles(styles: ɵStyleData | ɵStyleData[]): ɵStyleData {
  const /** @type {?} */ normalizedStyles: ɵStyleData = {};
  if (Array.isArray(styles)) {
    styles.forEach(data => copyStyles(data, false, normalizedStyles));
  } else {
    copyStyles(styles, false, normalizedStyles);
  }
  return normalizedStyles;
}
/**
 * @param {?} styles
 * @param {?} readPrototype
 * @param {?=} destination
 * @return {?}
 */
export function copyStyles(
    styles: ɵStyleData, readPrototype: boolean, destination: ɵStyleData = {}): ɵStyleData {
  if (readPrototype) {
    // we make use of a for-in loop so that the
    // prototypically inherited properties are
    // revealed from the backFill map
    for (let /** @type {?} */ prop in styles) {
      destination[prop] = styles[prop];
    }
  } else {
    Object.keys(styles).forEach(prop => destination[prop] = styles[prop]);
  }
  return destination;
}
/**
 * @param {?} element
 * @param {?} styles
 * @return {?}
 */
export function setStyles(element: any, styles: ɵStyleData) {
  if (element['style']) {
    Object.keys(styles).forEach(prop => element.style[prop] = styles[prop]);
  }
}
/**
 * @param {?} element
 * @param {?} styles
 * @return {?}
 */
export function eraseStyles(element: any, styles: ɵStyleData) {
  if (element['style']) {
    Object.keys(styles).forEach(prop => {
      // IE requires '' instead of null
      // see https://github.com/angular/angular/issues/7916
      element.style[prop] = '';
    });
  }
}
